<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import BaseTextInput from './BaseTextInput.vue';
import {
  autoPadDateSegment,
  formatGermanDate,
  parseLenientGermanDate,
} from '@/utils/formatting/dateInput';

interface Props {
  id?: string;
  name: string;
  label?: string;
  placeholder?: string;
  modelValue?: string | null;
  disabled?: boolean;
  readonly?: boolean;
  required?: boolean;
  autofocus?: boolean;
  autocomplete?: string;
  error?: boolean;
  errorMessage?: string;
  inputClass?: string;
  wrapperClass?: string;
  labelClass?: string;
  minDate?: Date | string;
  maxDate?: Date | string;
  locale?: string;
}

const props = withDefaults(defineProps<Props>(), {
  id: undefined,
  label: undefined,
  placeholder: undefined,
  modelValue: null,
  disabled: false,
  readonly: false,
  required: false,
  autofocus: false,
  autocomplete: 'off',
  error: false,
  errorMessage: undefined,
  inputClass: '',
  wrapperClass: '',
  labelClass: '',
  minDate: undefined,
  maxDate: undefined,
  locale: 'de',
});

const emit = defineEmits<{
  'update:modelValue': [value: string | null];
}>();

// Show closest month/year to today if outside of min/max range
const startDate = computed<Date | undefined>(() => {
  const normalize = (d?: Date | string): Date | undefined =>
    d === undefined ? undefined : d instanceof Date ? d : new Date(d);
  const min = normalize(props.minDate);
  const max = normalize(props.maxDate);
  const today = new Date();
  if (max && max < today) return max;
  if (min && min > today) return min;
  return undefined;
});
const dateValue = ref<Date | null>(null);
const textValue = ref<string>('');

/**
 * Text-input mode keeps focus in our input when the menu opens (otherwise the
 * calendar grabs focus and typing needs a second click). Parsing stays ours.
 */
const textInputOptions = { openMenu: 'open' as const };

function handleTextInput(event: Event) {
  const target = event.target as HTMLInputElement;
  const padded = autoPadDateSegment(target.value);
  if (target.value !== padded) {
    target.value = padded;
  }
  textValue.value = padded;

  const parsedDate = parseLenientGermanDate(padded);
  if (parsedDate && !areDatesEqual(parsedDate, dateValue.value)) {
    dateValue.value = parsedDate;
  }
}

function handleKeydown(event: KeyboardEvent, closeMenu: () => void) {
  if (event.key === 'Tab' || event.key === 'Escape') {
    closeMenu();
  } else if (event.key === 'Enter') {
    event.preventDefault();
    handleBlur();
    closeMenu();
  }
}

function handleBlur() {
  const trimmed = textValue.value.trim();

  if (!trimmed) {
    dateValue.value = null;
    return;
  }

  const parsedDate = parseLenientGermanDate(trimmed);

  if (parsedDate) {
    dateValue.value = parsedDate;
    textValue.value = formatGermanDate(parsedDate);
  } else {
    dateValue.value = null;
    emit('update:modelValue', trimmed);
  }
}

function areDatesEqual(d1: Date | null, d2: Date | null): boolean {
  if (d1 === d2) return true;
  if (!d1 || !d2) return false;
  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
}

watch(
  () => props.modelValue,
  (newValue) => {
    const newDate = typeof newValue === 'string' ? parseLenientGermanDate(newValue) : null;
    if (!areDatesEqual(newDate, dateValue.value)) {
      dateValue.value = newDate;
    }
    textValue.value = newValue || '';
  },
  { immediate: true }
);

watch(dateValue, (newValue) => {
  const formatted = newValue ? formatGermanDate(newValue) : '';

  textValue.value = formatted;

  if ((formatted || null) !== props.modelValue) {
    emit('update:modelValue', formatted || null);
  }
});
</script>

<template>
  <div :class="`relative date-range-picker base-date-input ${wrapperClass}`">
    <VueDatePicker
      v-model="dateValue"
      auto-apply
      :enable-time-picker="false"
      :min-date="minDate"
      :max-date="maxDate"
      :start-date="startDate"
      :locale="locale"
      month-name-format="long"
      :format="'dd.MM.yyyy'"
      :text-input="textInputOptions"
      :clearable="false"
      :teleport="true"
      :ui="{ menu: 'base-date-input-menu' }"
    >
      <template #dp-input="{ openMenu, closeMenu }">
        <div @keydown="(event: KeyboardEvent) => handleKeydown(event, closeMenu)">
          <BaseTextInput
            :id="id"
            :name="name"
            :label="label"
            :placeholder="placeholder"
            :model-value="textValue"
            :disabled="disabled"
            :readonly="readonly"
            :required="required"
            :autofocus="autofocus"
            :autocomplete="autocomplete"
            :error="error"
            :error-message="errorMessage"
            :input-class="inputClass"
            :label-class="labelClass"
            inputmode="decimal"
            :maxlength="10"
            @input="handleTextInput"
            @focus="openMenu"
            @blur="handleBlur"
          />
        </div>
      </template>
    </VueDatePicker>
  </div>
</template>

<style>
.base-date-input .dp__main {
  font-family: inherit;
}

.base-date-input-menu,
.base-date-input-menu .dp__theme_light,
.base-date-input-menu.dp__theme_light {
  --dp-background-color: #fff;
  --dp-menu-border-color: #fff;
  --dp-border-color: #fff;
  --dp-text-color: #000;
  --dp-hover-color: #f9f6ee;
  --dp-hover-text-color: #6c6b3c;
  --dp-hover-icon-color: #6c6b3c;
  --dp-primary-color: #f54b25;
  --dp-primary-text-color: #ffffff;
  --dp-secondary-color: #ebe7db;
  --dp-range-between-dates-background-color: #feede9;
  --dp-range-between-dates-text-color: #000;
  --dp-range-between-border-color: transparent;
  --dp-border-radius: 0;
  --dp-cell-border-radius: 0;
  --dp-font-size: 14px;
  --dp-font-family: 'Onest', sans-serif;
  --dp-primary-disabled-color: #f9f6ee;
  box-shadow: 0 6px 8px rgba(0, 0, 0, 0.15);
}

.base-date-input-menu .dp__calendar_header_item {
  text-transform: uppercase;
  font-weight: 400;
}

.base-date-input-menu .dp__main {
  justify-content: center;
}

.base-date-input-menu .dp__month_year_wrap button {
  font-weight: 600;
}

.base-date-input-menu .dp__menu_inner {
  gap: 1.4rem;
}

.base-date-input-menu .dp__overlay {
  background-color: #ffffff;
}
</style>
